<template>
  <section id="patient" class="patient">
    <div class="section-top-row align-items-center"
         :class="{ 'border-0': treatmentCard }">
      <div class="action-buttons-td position-relative">
        <div class="d-md-none d-inline-block">
          <h1>{{ patient ? patient.full_name : '' }}</h1>
        </div>
        <a class="d-md-none d-inline-block action-dots"
           @click="dotsPressed"><DotsSvg/></a>
        <div class="action-buttons-div d-md-flex row no-gutters align-items-center no-print"
             :class="{ expanded: mob_expanded, 'mobile-actions': isMobile }"
             v-click-outside="clickedOutside">
          <div class="col d-md-block d-none">
            <PatientHeader :patient="patient"
                           :patientLink="treatmentCard"
                           :visit="visit"
                           :showClientNote="showClientNote"/>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && !isPatient">
            <div class="d-inline-flex align-middle">
              <PatientAnketaDropdown/>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && !isPatient && rights.includes('contracts')">
            <div class="d-inline-flex align-middle">
              <a class="btn btn-themed gray" @click="contractPressed">
                <FileSvg class="mr-2 file-svg"/>{{ $t('contract') }}
              </a>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && rights.includes('payments')">
            <div class="d-inline-flex align-middle">
              <a class="btn btn-themed gray"
                 @click="paymentPressed">
                <DollarSvg class="mr-2 file-svg"/>{{ $t('payment') }}
              </a>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && rights.includes('patients_update')">
            <div class="d-inline-flex align-middle">
              <a class="btn btn-themed gray" @click="editPatient">
                <EditSvg class="mr-2"/>{{ $t('edit') }}
              </a>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && !isPatient">
            <div class="d-inline-flex align-middle">
              <a class="btn btn-themed gray" @click="printPressed">
                <PrintSvg class="mr-2 file-svg"/>{{ $t('print_noun') }}
              </a>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="!treatmentCard && rights.includes('add_records')">
            <div class="d-inline-flex align-middle">
              <a class="btn btn-themed d-inline-flex align-items-center" @click="addVisit">
                <CalendarSvg class="svg-white mr-2"/>
                {{ $t('add') }}
              </a>
            </div>
          </div>
          <div class="col-auto table-view-navigator"
               v-if="treatmentCard && visit">
            <a class="btn btn-themed d-inline-flex d-md-none align-items-center"
               @click="startTreatment"
               v-if="[1,2,3].includes(visit.records_statuses_id)">
              {{ $t('start_treatment') }}
            </a>
            <span v-if="visit.records_statuses_id == 4"
                  class="text-primary d-block font-weight-bold h-100">
              <span class="circle circle-primary sm mr-2"></span>{{ $t('in_chair') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <PatientCard :patientLoaded="patientLoaded"/>

    <div v-if="patient && patient.pinnedComments && patient.pinnedComments.length"
         style="max-width: 600px;">
      <PatientCommentTeaser v-for="c in patient.pinnedComments"
                            :key="`comment-${c.id}`"
                            :comment="c"
                            :collapsible="true"
                            :initialCollapseOpened="true"
                            @getComments="$store.commit('incrementDataTableCounter')"
                            class="mb-3">
      </PatientCommentTeaser>
    </div>

    <PatientNotesBlock v-if="patient && !showClientNote"
                       :only-important="true"
                       class="mb-3"
                       :notes="patient.client_notes"/>

    <div class="d-md-none d-block mobile-big-separator"></div>

    <div class="sub-menu no-print"
         v-if="!treatmentCard">
      <template v-for="(m, mind) in menu">
        <a class="pointer"
           tabindex="#"
           v-if="m.rightPopup && !rights.includes(m.rightPopup) && hasFreeTariff"
           :key="`a-link-${mind}}`"
           @click="$bvModal.show('module-not-in-tariff-modal')">
          {{ $t(m.title) }}
        </a>
        <router-link :to="{ name: m.to }"
                     v-else-if="(!m.right || rights.includes(m.right)) && !(isPatient && m.hiddenForPatient)"
                     :key="`patient-link-${mind}}`">
          {{ $t(m.title) }}
        </router-link>
      </template>
    </div>

    <div class="patient-add-wrap d-block no-print"
         v-if="canAdd && !['PatientPayments', 'PatientDepositTransactions', 'PatientNotifications','PatientNDFLs', 'PatientLaboratoryOrders', 'PatientCanban', 'PatientLeads'].includes($route.name)">
      <AddObject module="patients"
                 btnclass="btn-themed-outline"/>
    </div>

    <router-view class="no-print"
                 v-if="patient"
                 :canAdd="canAdd"/>

    <ScanModal/>
    <PatientAnketaModal/>
    <PatientContractSelectModal/>
    <PatientPrintModal/>
    <RefillBalanceModal/>
    <RefillBonusModal/>
    <PatientToPayModal/>
    <VisitPayModal/>
    <SendMailModal/>
    <AddNewVisitModalNew v-if="shouldUseNewVisitModal()"/>
    <AddNewVisitModal v-else/>
    <DeferredMessageModal/>
  </section>
</template>

<script setup>
import EditSvg from '@/assets/svg-vue/general/edit.svg'
import FileSvg from '@/assets/svg-vue/patients/file.svg'
import DollarSvg from '@/assets/svg-vue/patients/dollar.svg'
import PrintSvg from '@/assets/svg-vue/general/print.svg'
import CalendarSvg from '@/assets/svg-vue/calendar/calendar_plus.svg'
import DotsSvg from '@/assets/svg-vue/header/vertical_dots.svg'
import { useStore} from "vuex"
import ScanModal from "@/components/parts/patients/ScanModal"
import AddObject from "@/components/parts/datatable/AddObject"
import PatientAnketaModal from "@/components/parts/patients/PatientAnketaModal"
import PatientContractSelectModal from "@/components/parts/patients/PatientContractSelectModal"
import PatientPrintModal from "@/components/parts/patients/PatientPrintModal"
import RefillBalanceModal from "@/components/parts/patients/RefillBalanceModal"
import PatientToPayModal from "@/components/parts/patients/PatientToPayModal"
import VisitPayModal from "@/components/parts/patients/VisitPayModalNew"
import { EloquentService } from "@/services/api/eloquent.api.service"
import SendMailModal from "@/components/parts/patients/SendMailModal"
import AddNewVisitModal from "@/components/parts/calendar/AddNewVisitModal"
import AddNewVisitModalNew from "@/components/parts/calendar/AddNewVisitModalNew"
import { shouldUseNewVisitModal } from "@/services/calendar.helper"
import moment from "moment/moment"
import DeferredMessageModal from "@/components/parts/patients/DeferredMessageModal"
import RefillBonusModal from "@/components/parts/patients/RefillBonusModal.vue"
import PatientCommentTeaser from "@/components/parts/patients/PatientCommentTeaser"
import { countries } from "@/dictionaries/dictionary"
import PatientCard from "@/components/parts/patients/PatientCard.vue"
import PatientAnketaDropdown from "@/components/parts/patients/PatientAnketaDropdown.vue"
import PatientNotesBlock from "@/components/parts/patients/PatientNotesBlock.vue"
import {computed, getCurrentInstance, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {useRoute, useRouter} from "vue-router"
import {appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import PatientHeader from "@/components/parts/patients/PatientHeader.vue"

const store = useStore()
const route = useRoute()
const router = useRouter()

let bvModal = null
let i18n = null
let noty = null

const mob_expanded = ref(false)
const justOpened = ref(false)
const patientLoaded = ref(false)

const menuData = [
  { to: 'PatientVisits', title: 'visits' },
  { to: 'PatientFormula', title: 'formula' },
  { to: 'PatientPayments', title: 'payments', right: 'payments' },
  { to: 'PatientDepositTransactions', title: 'deposit', right: 'deposit' },
  { to: 'PatientFamilyAccount', title: 'family_account', right: 'family_account' },
  { to: 'PatientTreatmentPlan', title: 'treatment_plan', rightPopup: 'planning' },
  { to: 'PatientInsurancePolises', title: 'insurance', right: 'insurance' },
  { to: 'PatientScans', title: 'scans', right: 'scans' },
  { to: 'PatientNotes', title: 'notes', hiddenForPatient: true },
  { to: 'PatientDocuments', title: 'documents', right: 'documents_upload' },
  { to: 'PatientPersonal', title: 'personal_data' },
  { to: 'PatientComments', title: 'live_feed' },
  { to: 'PatientNotifications', title: 'notifications' },
  { to: 'PatientTasks', title: 'tasks', right: 'tasks' },
  { to: 'PatientFeedbacks', title: 'feedbacks', right: 'view_feedbacks' },
  { to: 'PatientLeads', title: 'leads', right: 'crm' },
  { to: 'PatientCanban', title: 'came_and_repeated', right: 'crm' },
  { to: 'PatientNDFLs', title: 'ndfl_statement', right: 'ndfl_form' },
  { to: 'PatientLaboratoryOrders', title: 'requirement_order', right: 'laboratories' },
  { to: 'PatientDiagnoCatStudies', title: 'Diagnocat AI', right: 'diagnocat' },
]

const clinic = computed(() => store.state.auth.clinic)
const patient = computed(() => store.state.patient.patient)
const rights = computed(() => store.state.auth.rights)
const visit = computed(() => store.state.patient.visit)
const counter = computed(() => store.state.datatable.dataTableCounter)
const user = computed(() => store.state.auth.user)
const isMobile = computed(() => store.state.dom.isMobile)
const hasFreeTariff = computed(() => store.getters.hasFreeTariff)
const isDentalClinic = computed(() => store.getters.isDentalClinic)
const isPatient = computed(() => store.getters.isPatient)

const menu = computed(() => {
  return menuData.filter(obj => {
    if(obj.to === 'PatientNDFLs' && clinic.value?.country_id !== countries.RUSSIA) return false
    if(obj.to === 'PatientFormula' && !isDentalClinic.value) return false
    return true
  })
})
const treatmentCard = computed(() => {
  return ['PatientVisitPage'].includes(route.name)
})
const canAdd = computed(() => {
  const routeName = route.name
  if([
    'PatientPersonal',
    'PatientVisitPage',
    'PatientTreatmentPlan',
    'PatientVisits',
    'PatientFamilyAccount',
    'PatientComments'
  ].includes(routeName)) {
    return false
  }
  if([
    'PatientPayments',
    'PatientDepositTransactions'
  ].includes(routeName) && !rights.value.includes('payments')) {
    return false
  }
  return true
})

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
  bvModal = instance?.ctx?._bv__modal
})

const addVisit = () => {
  let now = moment().format('YYYY-MM-DD HH')+":30:00"
  if(moment().format('mm') > 30) {
    now = moment().add(1, 'hours').format('YYYY-MM-DD HH')+":00:00"
  }
  store.commit('setNewVisit', {
    time: now,
    client_id: patient.value
  })
  bvModal.show('add-new-visit-modal')
}

const editPatient = () => {
  store.commit('setModule', 'patients')
  store.commit("setViewModel", 'patient')
  store.commit('setSelectedModel', copyObject(patient.value))
  store.commit('setShowModal', true)
}

const paymentPressed = () => {
  bvModal.show('patient-topay-modal')
}

const contractPressed = () => {
  bvModal.show('contract-modal')
}
const printPressed = () => {
  bvModal.show('patient-print-modal')
}

const startTreatment = async () => {
  if(!visit.value?.id) return
  let formData = new FormData()
  formData = appendFormdata(formData, { records_statuses_id: 4 })
  try {
    await EloquentService.update('record', visit.value.id, formData)
    noty.info(i18n.t('success_message'))
    store.commit('incrementDataTableCounter')
    visit.value.records_statuses_id = '4'
  } catch (e) {
    console.error(e)
  }
}

const dotsPressed = () => {
  justOpened.value = true
  mob_expanded.value = !mob_expanded.value
  setTimeout(() => {
    justOpened.value = false
  },20)
}

const clickedOutside = () => {
  if(!justOpened.value) {
    mob_expanded.value = false
  }
}

onMounted(async () => {
  store.commit('setPatient', null)
  store.commit('setModule', 'patients')
  if(isPatient.value && Number(route.params.id) !== Number(user.value?.id)) {
    router.push('/').catch(()=>{})
    return
  }
  await store.dispatch('getPatient', route.params.id)
  patientLoaded.value = true
  await store.dispatch('getDepositAccounts')
})

onBeforeUnmount(() => {
  store.commit('setPatient', null)
})

watch(counter, () => {
  store.dispatch('getPatient', route.params.id)
})

watch(route, (val) => {
  if(patient.value && Number(route.params.id) !== Number(patient.value.id)) {
    store.commit('setPatient', null)
    store.dispatch('getPatient', route.params.id)
    return
  }
  if(val.name === 'PatientVisits') {
    store.dispatch('getPatient', route.params.id)
  }
})

const showClientNote = computed(() => {
  return route?.name === 'PatientVisitPage'
})
</script>

<style scoped>
h1 {
  display: flex;
  align-items: center;
}

h1 > span {
  flex: 0 0 auto;
  white-space: nowrap;
}

.patient-note-span {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}

.patient-note-span :deep(.note-description) {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
